import React, { useEffect, useState } from 'react';
import { Smartphone, Target } from 'lucide-react';
import MatchList from './MatchList';
import PrizeInfo from './PrizeInfo';
import apiClient from '../services/apiClient';
import flagLg from '../assets/br-lg.png';

const DEFAULT_RULES = {
  exact_score: 3,
  correct_outcome: 1,
  knockout_draw_predicted_draw: 1,
  knockout_draw_exact_score: 1,
  knockout_draw_correct_winner: 1,
};

function Home() {
  const [rules, setRules] = useState(DEFAULT_RULES);

  useEffect(() => {
    apiClient.get('/settings/scoring-rules')
      .then(res => setRules(res.data))
      .catch(err => console.error('Error fetching scoring rules:', err));
  }, []);

  const koPredictedDraw = rules.knockout_draw_predicted_draw;
  const koExactScore = rules.knockout_draw_exact_score;
  const koCorrectWinner = rules.knockout_draw_correct_winner;
  const koAllThree = koPredictedDraw + koExactScore + koCorrectWinner;
  const koDrawAndWinner = koPredictedDraw + koCorrectWinner;

  return (
    <div className="space-y-8 overflow-hidden">
      {/* Hero Section - Main Banner */}
      <div className="bg-gradient-to-r from-primary via-primary-dark to-primary text-white rounded-lg p-6 md:p-8 text-center shadow-lg">
        <div className="flex justify-center mb-6">
          <img src={flagLg} alt="Bandeira do Brasil" width="96" height="64" className="rounded shadow-md" loading="lazy" />
        </div>
        <h1 className="text-4xl md:text-5xl font-bold mb-4">
          Bolão Copa do Mundo 2026
        </h1>
        <p className="text-xl md:text-2xl opacity-90">
          Teste seus conhecimentos de futebol e divirta-se com a família!
        </p>
      </div>

      {/* Prize Info Section */}
      <PrizeInfo />

      {/* How It Works Section */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-2xl font-bold text-primary mb-4 border-b-2 border-secondary pb-2">
          <Smartphone className="w-6 h-6 inline-block mr-2" aria-hidden="true" />Como Funciona
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          {/* Left Column */}
          <div className="space-y-3">
            <div className="flex items-start space-x-3">
              <div className="bg-green-100 text-primary rounded-full p-2 flex-shrink-0 mt-1">
                1
              </div>
              <div className="min-w-0">
                <h3 className="font-bold text-green-900">Cadastro e Login</h3>
                <p className="text-gray-700">
                  Registre-se com seu nome real (obrigatório para identificação). O nick, email, chave PIX e contato são opcionais. 
                  Você pode preencher a sua chave PIX e contato no seu perfil para facilitar o recebimento do prêmio, mas se não quiser, eu te procuro pelo nome! 
                  Você pode fazer o login utilizando seu nome completo, nick ou email.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="bg-green-100 text-primary rounded-full p-2 flex-shrink-0 mt-1">
                2
              </div>
              <div className="min-w-0">
                <h3 className="font-bold text-green-900">Pagamento</h3>
                <p className="text-gray-700">
                  Faça o pagamento através do Pix e envie o comprovante.
                </p>
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-3">
            <div className="flex items-start space-x-3">
              <div className="bg-green-100 text-primary rounded-full p-2 flex-shrink-0 mt-1">
                3
              </div>
              <div className="min-w-0">
                <h3 className="font-bold text-green-900">Palpites</h3>
                <p className="text-gray-700">
                  Faça seus palpites para cada partida até o momento do jogo.
                </p>
              </div>
            </div>

            <div className="flex items-start space-x-3">
              <div className="bg-green-100 text-primary rounded-full p-2 flex-shrink-0 mt-1">
                4
              </div>
              <div className="min-w-0">
                <h3 className="font-bold text-green-900">Classificação</h3>
                <p className="text-gray-700">
                  Acompanhe sua posição na classificação geral.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scoring System Section */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-2xl font-bold text-primary mb-4 border-b-2 border-secondary pb-2">
          <Target className="w-6 h-6 inline-block mr-2" aria-hidden="true" />Sistema de Pontuação
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          <div className="text-center bg-green-50 rounded-lg p-4 border-2 border-green-200">
            <div className="text-5xl font-bold text-primary-dark mb-2">{rules.exact_score}</div>
            <div className="font-bold text-green-900">{rules.exact_score === 1 ? 'PT' : 'PTS'}</div>
            <div className="text-gray-700 text-sm mt-1">Placar Exato</div>
          </div>

          <div className="text-center bg-yellow-50 rounded-lg p-4 border-2 border-yellow-200">
            <div className="text-5xl font-bold text-yellow-700 mb-2">{rules.correct_outcome}</div>
            <div className="font-bold text-yellow-900">{rules.correct_outcome === 1 ? 'PT' : 'PTS'}</div>
            <div className="text-gray-700 text-sm mt-1">Resultado Certo</div>
          </div>
        </div>
        <p className="text-sm text-gray-600 mt-4 text-center">
          Exemplo: Se você palpitou Brasil 2x1 Argentina e o resultado foi Brasil 2x1 Argentina, você ganha {rules.exact_score} {rules.exact_score === 1 ? 'ponto' : 'pontos'}.
          Se o resultado foi Brasil 1x0 Argentina, você ganha {rules.correct_outcome} {rules.correct_outcome === 1 ? 'ponto' : 'pontos'}.
        </p>

        {/* Knockout Draw Scoring */}
        <div className="mt-6 bg-orange-50 rounded-lg p-4 border-2 border-orange-200">
          <h3 className="font-bold text-orange-800 mb-3">Mata-mata com Empate</h3>
          <p className="text-sm text-gray-700 mb-3">
            Nas partidas eliminatórias, se houver empate no tempo normal você também informa quem avança. Os pontos somam (acertar o empate é pré-requisito):
          </p>
          <div className="grid grid-cols-3 gap-2 text-center text-sm mb-3">
            <div className="font-bold text-orange-700">Empate</div>
            <div className="font-bold text-orange-700">Placar</div>
            <div className="font-bold text-orange-700">Classificado</div>
            <div className="bg-white rounded px-2 py-1">+{koPredictedDraw} pt{koPredictedDraw === 1 ? '' : 's'}</div>
            <div className="bg-white rounded px-2 py-1">+{koExactScore} pt{koExactScore === 1 ? '' : 's'}</div>
            <div className="bg-white rounded px-2 py-1">+{koCorrectWinner} pt{koCorrectWinner === 1 ? '' : 's'}</div>
          </div>
          <p className="text-xs text-gray-600">
            Exemplo: Resultado 2x2, Brasil avança nos pênaltis.
            Palpite 2x2 Brasil = {koAllThree} {koAllThree === 1 ? 'pt' : 'pts'} (empate + placar + classificado).
            Palpite 1x1 Brasil = {koDrawAndWinner} {koDrawAndWinner === 1 ? 'pt' : 'pts'} (empate + classificado).
            Palpite 1x0 Brasil = 0 pts (errou o empate).
          </p>
        </div>
      </div>

      {/* Matches Section */}
      <div id="matches-section">
        <MatchList />
      </div>
    </div>
  );
}

export default Home;